import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useKeenSlider } from 'keen-slider/react';
import Link from 'next/link';
import { ICategorySliderWithNavBlock } from '@features/cms/typescript';
import styles from '@features/cms/components/categories/CategorySliderWithNavBlock/CategorySliderWithNavBlock.styles';
import SlideControl from '@components/SlideControl/SlideControl';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import Button from '@components/Button/Button';
import 'keen-slider/keen-slider.min.css';
import RenderMedia from '@utils/RenderMedia';
import thumbnailPlugin from '@utils/keenSlider/thumbnailPlugin';
import scrollToElementById from '@utils/scrollToElementById';
import { getRegion } from '@utils/misc';
import { TRegion } from '@typescript/index';
import sanitizeHtml from '@utils/sanitizeHtml';
import SliderIndicator from '@components/Slider/SliderIndicator';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
type PropsType = {
  data: ICategorySliderWithNavBlock;
  defaultBackground?: string;
  isFirst?: boolean;
};
const CategorySliderWithNavBlock = ({
  data,
  defaultBackground = theme.rawColors.neutralWhite,
  isFirst = false
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const region = getRegion('uppercase') as TRegion;
  const [shouldRenderArrows, setShouldRenderArrows] = useState({
    left: false,
    right: true
  });
  const [activeSlide, setActiveSlide] = useState(0);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const {
    code,
    background,
    marginTop,
    marginBottom,
    promoText,
    promoTextColor,
    slides,
    variant
  } = data;
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    renderMode: 'precision',
    drag: true,
    slides: slides.length
  });
  const updateRenderArrows = () => {
    setShouldRenderArrows({
      left: !!activeSlide,
      right: activeSlide !== slides.length - 1
    });
  };
  useEffect(() => {
    updateRenderArrows();
  }, [activeSlide]);
  useEffect(() => {
    slider.current?.moveToIdx(activeSlide);
  }, [activeSlide]);
  const handleChangeSlide = (id: number) => {
    if (!slider.current) return;
    slider.current.moveToIdx(id, false, {
      duration: 3000
    });
  };
  const [thumbnailRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 'auto',
      origin: 'auto',
      spacing: 24
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: {
          perView: 'auto',
          origin: 'auto',
          spacing: 32
        }
      },
      '(min-width: 1025px)': {
        slides: {
          perView: 'auto',
          origin: 'auto',
          spacing: 40
        }
      }
    }
  }, [thumbnailPlugin(slider, setActiveSlide)]);
  return <section id={code} sx={styles.styledSection(background || defaultBackground, marginTop, marginBottom)} ref={sectionRef} data-testid={code}>
      <div sx={styles.styledStickyContainer} data-testid="category-slider-with-nav-block-sticky-container">
        <div ref={sliderRef} className="fader" data-testid="category-slider-with-nav-block-slider">
          <div sx={styles.styledPromoText}>
            <Heading h={4} headingType={4} color={promoTextColor || theme.rawColors.neutralWhite} mobile uppercase>
              {promoText}
            </Heading>
          </div>
          {slides.map((mainSlider, index) => <div key={mainSlider.title} className="fader__slide" sx={styles.slide(activeSlide === index ? 1 : 0)}>
              {RenderMedia(mainSlider.image, mainSlider.video, activeSlide === index, mainSlider.imagesWithBlur, mainSlider.shortTitle, isFirst && index === 0)}
              <div sx={styles.contentContainer(mainSlider.verticalPosition, mainSlider.horizontalPosition)}>
                <div data-testid="category-slider-with-nav-block-content-container">
                  <Heading h={isFirst && index === 0 ? 1 : 2} display={variant === 'inspiration' ? 2 : 3} uppercase mobile color={theme.rawColors.neutralWhite} pb={24} dangerouslySetInnerHTML={sanitizeHtml(mainSlider.title)} />
                  {mainSlider.buttonUrl && mainSlider.buttonUrl.path && <>
                      {region === 'PL' && typeof window !== 'undefined' && typeof window.LiveChatWidget !== 'undefined' && (mainSlider.buttonUrl.path === '#livechat' || mainSlider.buttonUrl.path === '#livechat-voxbox') ? <Button variant={mainSlider.buttonVariant === 'solidGraphite' || mainSlider.buttonVariant === 'solidWhite' ? 'default' : 'outlined'} variantColor={mainSlider.buttonVariant === 'outlinedGraphite' || mainSlider.buttonVariant === 'solidGraphite' ? 'dark' : 'white'} cy={`${code}-livechat-${index}`}>
                          <span data-id={mainSlider.buttonUrl.path === '#livechat' ? 'VeAR_qiV4Vl' : 'eei5o0iwpA2Z'} className="livechat_button" dangerouslySetInnerHTML={{
                    __html: `<a onclick="event.preventDefault(); if (LiveChatWidget !== undefined) { LiveChatWidget.call('maximize');return false; }" href="https://www.livechat.com/?utm_source=chat_button&utm_medium=referral&utm_campaign=lc_12811512">${mainSlider.buttonUrl.text || t('see')}</a>`
                  }} />
                        </Button> : <>
                          {mainSlider.buttonUrl.path && mainSlider.buttonUrl.path.slice(0, 1) === '#' ? <Button onClick={() => scrollToElementById(mainSlider.buttonUrl.path.substring(1))} variant={mainSlider.buttonVariant === 'solidGraphite' || mainSlider.buttonVariant === 'solidWhite' ? 'default' : 'outlined'} variantColor={mainSlider.buttonVariant === 'outlinedGraphite' || mainSlider.buttonVariant === 'solidGraphite' ? 'dark' : 'white'} cy={`${code}-button-${index}`}>
                              {mainSlider.buttonUrl.text || t('see')}
                            </Button> : <Link href={prepareURLByCMSLink(mainSlider.buttonUrl)}>
                              <Button variant={mainSlider.buttonVariant === 'solidGraphite' || mainSlider.buttonVariant === 'solidWhite' ? 'default' : 'outlined'} variantColor={mainSlider.buttonVariant === 'outlinedGraphite' || mainSlider.buttonVariant === 'solidGraphite' ? 'dark' : 'white'} cy={`${code}-button-${index}`}>
                                {mainSlider.buttonUrl.text || t('see')}
                              </Button>
                            </Link>}
                        </>}
                    </>}
                </div>
              </div>
            </div>)}
        </div>
        {shouldRenderArrows.right && <SlideControl direction="next" onClick={() => slider.current?.next()} />}
        {shouldRenderArrows.left && <SlideControl direction="prev" onClick={() => slider.current?.prev()} />}
        <div sx={styles.navContainer}>
          <div ref={thumbnailRef} className="keen-slider thumbnail" sx={styles.sliderNavigationBox}>
            {slides.map((nav, index) => <button type="button" key={nav.shortTitle} sx={styles.navButtons} className={`keen-slider__slide number-slide${index + 1}`} onClick={() => handleChangeSlide(index)} data-testid={`categorySliderNav-${nav.shortTitle}`}>
                <Heading h={6} headingType={6} color={theme.rawColors.neutralWhite} pb={4} uppercase>
                  {nav.shortTitle}
                </Heading>
              </button>)}
          </div>
        </div>
        <SliderIndicator slidesLength={slides.length} current={activeSlide + 1} onClick={handleChangeSlide} />
      </div>
    </section>;
};
export default CategorySliderWithNavBlock;